<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end form-offcanvas"
    tabindex="-1"
    id="offcanvaInfo"
    ref="offcanvaInfo"
    data-bs-backdrop="static"
  >
    <!-- data-bs-backdrop="static" -->
    <div class="offcanvas-header border-bottom bg-white py-3">
      <h6 class="offcanvas-title fw-bold mb-0 d-flex align-items-center gap-2">
        <i class="fa-solid fa-book-open text-theme" />
        {{ $t('Recipe Detail')  }}
      </h6>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body bg-body-secondary p-3"
      v-else
    >
      <!-- ───── Hero ───── -->
      <div class="form-card">
        <div class="px-3 py-3">
          <div class="fw-bold d-flex align-items-center flex-wrap gap-2" style="font-size: 18px;">
            {{ data.name }}
            <span
              style="text-transform: capitalize"
              :class="['badge', data.active? 'bg-success': 'bg-danger']"
            >
              {{ data.active? $t('Active'): $t('Inactive') }}
            </span>
          </div>
          <div class="text-secondary small mt-2">
            {{ $t('Last Update') + ': ' + formatDateTime(data.updated_at) }}
          </div>
        </div>
      </div>

      <!-- ───── ข้อมูลสูตร ───── -->
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-circle-info" />
          <span>{{ $t('Recipe Information') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row g-3" style="font-size: 13px;">
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Cost') }}</div>
              <div class="fw-medium">{{ formatDigit(data.cost) }} ฿</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Production Count') }}</div>
              <div class="fw-medium">{{ data.production_count? data.production_count: '-' }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Last Used') }}</div>
              <div class="fw-medium">{{ data.last_used_at? formatDate(data.last_used_at): '-' }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Create By') }}</div>
              <div class="fw-medium">{{ data.created_by }}</div>
            </div>
            <div class="col-12" v-if="data.description">
              <div class="text-secondary">{{ $t('Description') }}</div>
              <div class="ql-editor p-0" v-html="data.description" />
            </div>
          </div>
        </div>
      </div>
      <!-- ───── Inputs ───── -->
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-arrow-right-to-bracket" />
          <span>{{ $t('Inputs (Ingredients to consume)') }}</span>
        </div>
        <div class="overflow-x-auto">
          <vue3-easy-data-table
            ref="datatableItemInputs"
            :headers="datatableItemInputs.headers($t)"
            :items="datatableItemInputs.items"
            :loading="datatableItemInputs.loading"
            :theme-color="'#515151'"
            :hide-footer="true"
            :hide-rows-per-page="true"
            :empty-message="$t('No Available Data')"
            :table-min-height="null"
            :body-item-class-name="bodyItemClassNameFunction"
            table-class-name='customize-table-hide-footer-in-offcanvas'
          >
            <template #header-quantity="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #item-quantity="item">
              {{ formatDigit(item.quantity) }}
            </template>
            <template #item-unit="item">
              {{ $t(item.unit) }}
            </template>
            <template #loading>
              <i class="fa-solid fa-spinner fa-spin fa-2xl" />
            </template>
          </vue3-easy-data-table>
        </div>
      </div>
      <!-- ───── Outputs ───── -->
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-arrow-right-from-bracket" />
          <span>{{ $t('Outputs (Results produced)') }}</span>
        </div>
        <div class="overflow-x-auto">
          <vue3-easy-data-table
            ref="datatableItemOutputs"
            :headers="datatableItemOutputs.headers($t)"
            :items="datatableItemOutputs.items"
            :loading="datatableItemOutputs.loading"
            :theme-color="'#515151'"
            :hide-footer="true"
            :hide-rows-per-page="true"
            :empty-message="$t('No Available Data')"
            :table-min-height="null"
            :body-item-class-name="bodyItemClassNameFunction"
            table-class-name='customize-table-hide-footer-in-offcanvas'
          >
            <template #header-quantity="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #header-shelf_life_value="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #header-cost_allocation_percent="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #item-quantity="item">
              {{ formatDigit(item.quantity) }}
            </template>
            <template #item-shelf_life_value="item">
              {{ item.shelf_life_value + ' ' }}
              <span
                style="text-transform: capitalize"
              >
                {{ $t(item.shelf_life_unit) }}
              </span>
            </template>
            <template #item-unit="item">
              <span
                style="text-transform: capitalize"
              >
                {{ $t(item.unit) }}
              </span>
            </template>
            <template #item-cost_allocation_percent="item">
              {{ item.cost_allocation_percent + ' %' }}
            </template>
            <template #loading>
              <i class="fa-solid fa-spinner fa-spin fa-2xl" />
            </template>
          </vue3-easy-data-table>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading"
    >
      <div
        class="btn-group dropdown"
      >
        <button
          type="button"
          class="btn btn-sm btn-theme dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <i class="fa-solid fa-ellipsis me-1" :class="isHovered? 'fa-fade': ''" />
          {{ $t('More Action') }}
        </button>
        <ul class="dropdown-menu">
          <li>
            <a
              class="dropdown-item"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              @click="() => {
                $parent.offcanvasFroms.open = true;
                $parent.offcanvasFroms.id = id;
                $parent.offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
              {{ $t('Edit') }}
            </a>
          </li>
          <li>
            <a
              class="dropdown-item"
              @click="handleDelete(id, data.name)"
            >
              <i class="fa-solid fa-trash-can" />
              {{ $t('Delete') }}
            </a>
          </li>
        </ul>
      </div>
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import Select2 from 'vue3-select2-component';
import { InventoryRecipes } from '@/services/endpoint/inventory-recipes.service';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import moment from "moment";
import numeral from 'numeral';
import { distance } from '@/store/date-fns'
import Popper from "vue3-popper";
import FieldItemInputs from "./assets/template/datatable/FieldItemInputs";
import FieldItemOutputs from "./assets/template/datatable/FieldItemOutputs";

export default {
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      datatableItemInputs: {
        loading: false,
        headers: FieldItemInputs,
        items: []
      },
      datatableItemOutputs: {
        loading: false,
        headers: FieldItemOutputs,
        items: []
      },
      isHovered: false,
      loadingCard: true,
      loading: true,
      data: {
        name: '',
        code: '',
        quantity: 0,
        cost: 0,
        unit_id: '1',
      },
      tabCards: [
        {
          id: 1,
          name: 'Activity Logs',
          disabled: false
        },
        {
          id: 2,
          name: 'Unusable',
          disabled: false
        }
      ],
      selectTabCard: 1
    }
  },
  components: {
    Vue3EasyDataTable,
    Select2,
    PlaceholderContent,
    Ladda,
    Popper
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'border-0';
      const columnStart = ['name', 'unit'];
      const columnCenter = [];
      const columnEnd = ['quantity', 'shelf_life_value', 'cost_allocation_percent'];
      if (columnStart.includes(column)) {
        className += ' text-start';
      }
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getInfoData() {
      this.loading = true;
      await InventoryRecipes.getInfo(this.id).then(res => {
        this.data = res.data.data;
        this.datatableItemInputs.items = res.data.data.inputs;
        this.datatableItemOutputs.items = res.data.data.outputs;
        this.loading = false;
      }).catch(err => {
			  throw err;
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    dateDistance(value) {
      return distance(value);
    },
    async handleDelete(id, recipeName) {
      try {
        let self = this;
        let title = this.$t("Delete this Recipe") + '?';
        let html = this.$t('You are about to permanently delete the recipe') + ' ' + recipeName + ' ' + this.$t('This action cannot be undone. Are you sure') + '?';
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return InventoryRecipes.delete(id)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvaInfo).offcanvas('show');

    $(this.$refs.offcanvaInfo).on('shown.bs.offcanvas',async () => {
      try {
        await this.getInfoData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaInfo).on('hidden.bs.offcanvas', () => {
      this.$parent.formInfo.open = false;
    });
  },
  computed: {

  },
  watch: {
  }
}
</script>
